"use client";

import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { deriveAttributeKey } from "@/lib/categories/attribute-key";
import type { AttributeRow } from "./use-category-drafts";
import { CustomerSlugInput } from "./customer-slug-input";
import { AttributeValueEditor } from "./attribute-value-editor";
import { languageName, type LanguageCode } from "@/constants/languages";

interface AttributeListProps {
  rows: AttributeRow[];
  readOnly: boolean;
  languages: LanguageCode[];
  /** Keys referenced by the title template, marked so the link is visible. */
  usedKeys: ReadonlySet<string>;
  onChange: (rows: AttributeRow[]) => void;
}

/**
 * Attributes, asked for by name rather than by pipeline id.
 *
 * The id is derived from the name while the attribute is new and frozen once
 * saved, the way a web address is: renaming an attribute afterwards must not
 * silently break a title template that references it.
 */
export function AttributeList({
  rows,
  readOnly,
  languages,
  usedKeys,
  onChange,
}: AttributeListProps) {
  const seen = new Map<string, number>();
  rows.forEach((row, i) => {
    const label = row.label.trim().toLowerCase();
    if (label && !seen.has(label)) seen.set(label, i);
  });

  const update = (index: number, patch: Partial<AttributeRow>) => {
    onChange(
      rows.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, ...patch };
        if (patch.label !== undefined && row.isNew) {
          next.key = deriveAttributeKey(patch.label);
        }
        return next;
      }),
    );
  };

  const remove = (index: number) => onChange(rows.filter((_, i) => i !== index));

  if (!rows.length) {
    return (
      <p className="text-sm italic text-muted-foreground">
        No attributes yet. Add one to get started.
      </p>
    );
  }

  return (
    <div className="grid gap-2">
      {rows.map((row, i) => {
        const label = row.label.trim().toLowerCase();
        const isDuplicate = !!label && seen.get(label) !== i;
        const isUsed = usedKeys.has(row.key);
        return (
          <div key={row.uid} className="grid gap-2 rounded-md border p-3">
            <div className="grid grid-cols-[minmax(150px,220px)_1fr_30px] items-center gap-2">
              <div className="relative">
                <Input
                  value={row.label}
                  placeholder="e.g. Seat height"
                  disabled={readOnly}
                  autoFocus={row.isNew && !row.label && !row.description}
                  aria-invalid={isDuplicate}
                  title={row.key ? `Youzu refers to this as ${row.key}` : undefined}
                  className={cn(
                    isUsed && "pr-16",
                    isDuplicate && "border-destructive",
                  )}
                  onChange={(e) => update(i, { label: e.target.value })}
                />
                {isUsed && (
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-pink-200 bg-pink-50 px-1.5 text-[10px] text-pink-700">
                    in titles
                  </span>
                )}
              </div>

              <Input
                value={row.description}
                placeholder="What should Youzu look for?"
                disabled={readOnly}
                onChange={(e) => update(i, { description: e.target.value })}
              />

              {readOnly ? (
                <span />
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  aria-label={`Remove ${row.label || "this attribute"}`}
                  onClick={() => remove(i)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              {languages.map((language) => (
                <CustomerSlugInput
                  key={language}
                  id={`${row.uid}-key-slug-${language}`}
                  label={`${languageName(language)} attribute slug`}
                  value={row.attribute_slug?.[language] ?? ""}
                  disabled={readOnly}
                  onChange={(value) =>
                    update(i, {
                      attribute_slug: {
                        ...(row.attribute_slug ?? {}),
                        [language]: value,
                      },
                    })
                  }
                />
              ))}
            </div>
            <AttributeValueEditor
              id={row.uid}
              name={row.label || "Attribute"}
              valueType={row.valueType}
              choices={row.choices}
              languages={languages}
              disabled={readOnly}
              onChange={(settings) => update(i, settings)}
            />
            {isDuplicate && (
              <p className="text-xs text-destructive">
                You already have an attribute called “{row.label.trim()}”. Give this one
                a different name.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
